import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { MediaQuery, Sticky, StickyCallToAction, Price } from '@grandvisionhq/common';
import { Hr } from '@grandvisionhq/elements';
import { useProductDetailSlots, useVto, useCmsData, getActiveBrandPromotion, ProductDetailTitleView, PrescriptionAvailabilityIndicator, ProductDetailDescriptionView, ProductDetailFeaturesView, BrandPromotionView, ProductDetailDisclaimer, ProductDetailPrice, ProductDiscountLabel, Discount, SunglassesRxableSelect, TryOnButton, VirtualMirrorModal, ProductDetailUsps, RxGlassesCTA, NonRxProductCTA } from '@grandvisionhq/fun-products';
import { useReviewsContext, Actions, ReviewSnippet } from '@grandvisionhq/reviews';
import { DeliveryIndicator } from '@grandvisionhq/shipping';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { getLocalizedValue } from '@grandvisionhq/utils';
import { getActiveVariant, getVariants, getSunVariantPickerLabel } from '../../helpers.js';
import { RxGlassesConfigurator } from '../../Prescription/Glasses/Configurator/RxGlassesConfigurator.js';
import { pushPdpEventToDataLayer } from '../../utils/DataLayer/data-layer.js';
import { mapVirtualMirrorVariants } from '../../utils/map-virtual-mirror-variants.js';
import { useProductViewHistory } from '../../utils/use-product-view-history.js';
import { triggerDataLayerEvent } from './data-layer-utils.js';
import { useBrandLink } from '../utils/use-brand-link.js';
import { useProductDetailContext } from '../Context/ProductDetailContext.js';
import { getProductFeatures } from '../utils/get-product-features.js';
import { ProductImageGalleryContainer } from '../../ProductImageGallery/ProductImageGalleryContainer.js';
import { ProductDetailSpecificationContainer } from '../../ProductDetailSpecification/ProductDetailSpecificationContainer.js';
import { ProductLabelTag } from '../Components/ProductLabelTag/ProductLabelTag.js';
import { VariantPickerContainer } from '../Components/VariantPicker/VariantPickerContainer.js';

const WITH_RX = 'with-rx';
const WITHOUT_RX = 'without-rx';
const isRxRadioSelection = (value)=>value === WITH_RX || value === WITHOUT_RX
;
const Sunglasses = ({ activeSku , addToCartRef , product , showStickyButton , variant , deliveryResolver , stickyTop , onCTAClick , onPriceChange , opticianForm , secondaryCallToAction , handleConversionEvent , storesRoute , bookAppointmentRoute ,  })=>{
    var ref3, ref1;
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const { getLabel , currency , locale  } = useIntl();
    const productViewHistory = useProductViewHistory();
    const reviews = useReviewsContext();
    const brandLink = useBrandLink(product);
    const [selectWithRxLens, setSelectWithRxLens] = React.useState(WITHOUT_RX);
    const [isConfiguratorOpen, setIsConfiguratorOpen] = React.useState(false);
    const { isDeliveryFullDateEnabled , isProductLabelEnabled , featureConfigs  } = useFeatureToggles();
    const { Slots  } = useProductDetailSlots();
    const { isVtoActive , isVtoFeatureEnabled  } = useVto();
    const { brandPromotions , disclaimer , rxGlassesConfiguratorDocument , usps: productTypeUsps ,  } = useCmsData();
    const { colorFeatureIcons  } = useProductDetailContext();
    const variants = [
        product.masterVariant,
        ...product.variants
    ];
    const { channelAvailability , excludedFromCheckout , price , isOfferedWithPrescriptions , tag  } = variant;
    // Add SKU to viewed product history
    React.useEffect(()=>{
        // We only want 1 SKU(variant) per product to be in the view history
        productViewHistory.remove(variants.map((v)=>v.sku || ''
        ));
        productViewHistory.add(variant.sku);
    }, [
        variants.length,
        variant.sku
    ]);
    // Make sure the ReviewsDisplay component gets updated with the currently active SKU
    React.useEffect(()=>{
        if (reviews.enabled) {
            reviews.dispatch(Actions.updateReviewDisplay({
                variantId: activeSku
            }));
        }
    }, [
        activeSku,
        reviews.enabled
    ]);
    React.useEffect(()=>{
        triggerDataLayerEvent('view step', {
            isWithRx: selectWithRxLens === WITH_RX
        });
    // Deps intentionally left empty. This effect should only run once.
    }, []);
    React.useEffect(()=>{
        pushPdpEventToDataLayer(DataLayer.DataLayerTypes.PurchaseType.OneTime, product, activeSku, currency);
    }, [
        activeSku
    ]);
    const shouldOpenConfigurator = isOfferedWithPrescriptions && selectWithRxLens === WITH_RX;
    const onRxRadioChange = React.useCallback((newSelection)=>{
        if (isRxRadioSelection(newSelection) && selectWithRxLens !== newSelection) {
            setSelectWithRxLens(newSelection);
            triggerDataLayerEvent('click on button', {
                isWithRx: newSelection === WITH_RX
            });
        }
    }, [
        selectWithRxLens
    ]);
    /**
   * RxGlassesConfigurator utils
   */ const openConfiguratorButtonRef = React.useRef(null);
    const handleConfiguratorClose = React.useCallback(()=>{
        var ref;
        setIsConfiguratorOpen(false);
        openConfiguratorButtonRef === null || openConfiguratorButtonRef === void 0 ? void 0 : (ref = openConfiguratorButtonRef.current) === null || ref === void 0 ? void 0 : ref.focus();
    }, [
        openConfiguratorButtonRef
    ]);
    const handleConfiguratorOpen = React.useCallback(()=>{
        handleConversionEvent();
        setIsConfiguratorOpen(true);
    }, []);
    const CTAButton = shouldOpenConfigurator ? /*#__PURE__*/ React.createElement(RxGlassesCTA, {
        bookAppointmentRoute: bookAppointmentRoute,
        action: channelAvailability === 'ONLINE' ? 'OPEN_CONFIGURATOR' : 'OPEN_CONFIGURATOR_UNAVAILABLE',
        onClickWhenOrderable: handleConfiguratorOpen,
        buttonRef: openConfiguratorButtonRef,
        handleConversionEvent: handleConversionEvent
    }) : /*#__PURE__*/ React.createElement(NonRxProductCTA, {
        storesRoute: storesRoute,
        action: channelAvailability === 'ONLINE' ? 'ADD_TO_CART' : 'ADD_TO_CART_UNAVAILABLE',
        handleConversionEvent: handleConversionEvent,
        addToCartConfig: {
            activeSku,
            onSuccess: onCTAClick
        }
    });
    const activeVariant = getActiveVariant(product, activeSku);
    const productFeatures = getProductFeatures(variant);
    const isVirtualMirrorEnabled = activeVariant.vtoEnabled && (featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.virtualTryOn) === 'VIRTUAL_MIRROR';
    const brandLogo = (ref3 = getActiveBrandPromotion(variant.brand, brandPromotions)) === null || ref3 === void 0 ? void 0 : ref3.logo;
    const mappedVTOVariants = isVirtualMirrorEnabled ? mapVirtualMirrorVariants(variants, activeSku, locale, brandLogo) : undefined;
    var ref2, _discountedPrice;
    return /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__main product-detail__main--sunglasses"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "desktop-hidden product-detail__header--mobile"
    }, /*#__PURE__*/ React.createElement(ProductDetailTitleView, {
        title: `${variant.brand} ${variant.modelName}`,
        subtitle: variant.brand,
        href: brandLink,
        brandName: variant.brand,
        brandPromotions: brandPromotions
    }), isSmallScreen && /*#__PURE__*/ React.createElement(ReviewSnippet, {
        className: "product-detail__review-snippet",
        productId: product.masterVariant.sku,
        variantId: activeSku
    })), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__image-gallery"
    }, isOfferedWithPrescriptions && /*#__PURE__*/ React.createElement(PrescriptionAvailabilityIndicator, {
        className: "product-detail__prescription-availability-indicator"
    }), /*#__PURE__*/ React.createElement(ProductImageGalleryContainer, null)), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__description"
    }, /*#__PURE__*/ React.createElement(ProductDetailDescriptionView, {
        header: variant.header,
        description: variant.description,
        usps: variant.usps
    }, /*#__PURE__*/ React.createElement(ProductDetailFeaturesView, {
        productFeatures: productFeatures,
        colorFeatureIcons: colorFeatureIcons
    }), brandPromotions && /*#__PURE__*/ React.createElement(BrandPromotionView, {
        brandPromotions: brandPromotions,
        brandName: variant.brand,
        className: "product-detail__brand-promotion--clickable",
        isClickable: true
    }))), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__specification"
    }, /*#__PURE__*/ React.createElement(ProductDetailSpecificationContainer, null)), disclaimer && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__disclaimer"
    }, /*#__PURE__*/ React.createElement(ProductDetailDisclaimer, null)), /*#__PURE__*/ React.createElement("section", {
        className: "product-detail__aside",
        "aria-label": `${variant.brand} ${variant.modelName}`
    }, /*#__PURE__*/ React.createElement(Sticky, {
        top: stickyTop || 0,
        bottomBoundary: '.product-detail',
        enabled: !isSmallScreen
    }, /*#__PURE__*/ React.createElement("div", {
        className: "mobile-hidden"
    }, /*#__PURE__*/ React.createElement(ProductDetailTitleView, {
        title: `${variant.brand} ${variant.modelName}`,
        subtitle: variant.brand,
        href: brandLink,
        brandPromotions: brandPromotions,
        brandName: variant.brand
    })), !isProductLabelEnabled && tag && tag.value && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__product-tag"
    }, /*#__PURE__*/ React.createElement(ProductLabelTag, null)), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__specification-prices",
        role: "group",
        "aria-label": getLabel('products.screenreader.priceInformation')
    }, price && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__price-container"
    }, /*#__PURE__*/ React.createElement(ProductDetailPrice, {
        originalPrice: price.originalPrice,
        discountedPrice: price.discountedPrice,
        currency: currency
    })), isProductLabelEnabled && /*#__PURE__*/ React.createElement(ProductDiscountLabel, {
        title: variant.discountText
    }), !isProductLabelEnabled && variant.discounted && /*#__PURE__*/ React.createElement(Discount, {
        label: variant.discountText,
        discount: variant.discounted,
        isWide: true
    }))), Slots.AfterPrice ? /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__after-price-slot"
    }, /*#__PURE__*/ React.createElement(Slots.AfterPrice, null)) : null, !isSmallScreen && /*#__PURE__*/ React.createElement(ReviewSnippet, {
        className: "product-detail__review-snippet",
        productId: product.masterVariant.sku,
        variantId: activeSku
    }), /*#__PURE__*/ React.createElement(VariantPickerContainer, {
        variantItems: getVariants(variants, getLocalizedValue(product.slug, locale), variant.productType, locale),
        activeVariantSku: variant.sku,
        activeVariantLabel: getSunVariantPickerLabel(variant),
        enableStockIndicator: !excludedFromCheckout,
        stockCount: (ref2 = (ref1 = variant.availability) === null || ref1 === void 0 ? void 0 : ref1.availableQuantity) !== null && ref2 !== void 0 ? ref2 : 0,
        isAllowBackOrder: !!variant.allowBackOrder,
        channelAvailability: variant.channelAvailability
    }), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__cart-details"
    }, isOfferedWithPrescriptions && /*#__PURE__*/ React.createElement(SunglassesRxableSelect, {
        onChange: onRxRadioChange,
        selectedValue: selectWithRxLens
    }), !excludedFromCheckout && isDeliveryFullDateEnabled && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__cart-details__delivery-indicator"
    }, /*#__PURE__*/ React.createElement(DeliveryIndicator, {
        view: "text",
        items: [
            {
                sku: activeSku,
                quantity: 1
            }
        ]
    })), /*#__PURE__*/ React.createElement("div", {
        ref: addToCartRef
    }, CTAButton, secondaryCallToAction && secondaryCallToAction(variant), activeVariant.vtoEnabled && isVtoFeatureEnabled && /*#__PURE__*/ React.createElement(TryOnButton, null)), isVirtualMirrorEnabled && isVtoActive && /*#__PURE__*/ React.createElement(VirtualMirrorModal, {
        variants: mappedVTOVariants
    }), Slots.AfterCTA ? /*#__PURE__*/ React.createElement(Slots.AfterCTA, null) : null), isSmallScreen && showStickyButton && variant.price && /*#__PURE__*/ React.createElement(StickyCallToAction, {
        button: CTAButton,
        price: /*#__PURE__*/ React.createElement(Price, {
            value: (_discountedPrice = variant.price.discountedPrice) !== null && _discountedPrice !== void 0 ? _discountedPrice : variant.price.originalPrice,
            currency: currency,
            centAmount: true,
            type: variant.price.discountedPrice ? 'discount' : 'default'
        })
    }), (!excludedFromCheckout || productTypeUsps) && /*#__PURE__*/ React.createElement(Hr, {
        className: "product-detail__usp-divider"
    }), !excludedFromCheckout && !isDeliveryFullDateEnabled && /*#__PURE__*/ React.createElement(DeliveryIndicator, {
        availability: [
            activeVariant.availability
        ],
        deliveryResolver: deliveryResolver,
        view: "icon",
        className: "product-detail__delivery-indicator"
    }), /*#__PURE__*/ React.createElement(ProductDetailUsps, null))), /*#__PURE__*/ React.createElement(RxGlassesConfigurator, {
        id: "rxSun",
        frame: product,
        frameSku: activeSku,
        isOpen: isConfiguratorOpen,
        triggerButtonRef: openConfiguratorButtonRef,
        onModalClose: handleConfiguratorClose,
        onPriceChange: onPriceChange,
        opticianForm: opticianForm,
        configuratorDocument: rxGlassesConfiguratorDocument,
        usps: productTypeUsps
    }));
};
var Sunglasses$1 = Sunglasses;

export { Sunglasses, Sunglasses$1 as default, isRxRadioSelection };
//# sourceMappingURL=Sunglasses.js.map
